import { Component, OnInit, OnDestroy, Optional, AfterViewInit, ElementRef, ViewChild, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { enterPortalApp } from '../../enter-portal-app';
import { MfaMonitorService } from '../../services/mfa-monitor.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-two-factor',
  templateUrl: './two-factor.component.html',
  styleUrls: ['./two-factor.component.scss']
})
export class TwoFactorComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  twoFactorForm: FormGroup;
  isLoading$: Observable<boolean>;
  hasError = false;
  errorMessage = '';
  returnUrl: string = '/';
  qrCodeUrl: string = '';
  backupCodes: string[] = [];
  showBackupCodeField = false;
  mfaType: 'totp' | 'email' = 'totp';
  emailSent = false;
  secretKey: string = '';
  showSecretKey = false;
  isModalMode = false;
  mfaEnabled = false;
  shake = false;
  readonly codeSlots = [1, 2, 3, 4, 5, 6];

  // Headless dialog support
  @ViewChild('dialogRoot') dialogRoot?: ElementRef<HTMLDivElement>;
  private trapActive = false;
  private focusableElements: HTMLElement[] = [];
  private originalScrollY = 0;

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private mfaMonitorService: MfaMonitorService,
    @Optional() public activeModal?: NgbActiveModal
  ) {
    this.isLoading$ = this.authService.isLoading$;
    this.initForm();
  }

  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    
    // Check if this is modal mode (triggered by MFA monitor)
    const modalParam = this.route.snapshot.queryParams['modal'];
    this.isModalMode = modalParam === 'true' || !!this.activeModal;
    
    // Track MFA status from current user to prevent duplicate setup calls
    this.authService.currentUserSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: any) => {
        this.mfaEnabled = !!(user?.mfa_status?.enabled);
      });
    
    // After email login MFA challenge, only verify — enrollment uses /auth/qr-setup.
    // Never GET /auth/mfa/setup-totp here (API is POST-only; GET 403/404s).
  }

  ngAfterViewInit(): void {
    // Only activate custom headless behaviors when NOT hosted by NgbModal
    if (this.isModalMode && !this.activeModal) {
      this.activateScrollLock();
      this.initFocusTrap();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.trapActive) {
      this.deactivateScrollLock();
      this.teardownFocusTrap();
    }
  }

  closeModal(): void {
    this.mfaMonitorService.closeMfaDialog();
  }

  get otpPrefix(): 'code' | 'emailCode' {
    return this.mfaType === 'email' ? 'emailCode' : 'code';
  }

  get submitDisabled(): boolean {
    if (this.showBackupCodeField) {
      return !!this.twoFactorForm.get('backupCode')?.invalid;
    }
    return !this.isCodeValid(this.otpPrefix);
  }

  get submitLabelKey(): string {
    if (this.showBackupCodeField) {
      return 'AUTH.MFA.USE_BACKUP';
    }
    return this.mfaType === 'email' ? 'AUTH.MFA.VERIFY_EMAIL' : 'AUTH.MFA.VERIFY';
  }

  get showIncompleteCodeError(): boolean {
    if (this.hasError || !this.twoFactorForm || this.isCodeValid(this.otpPrefix)) {
      return false;
    }
    return this.codeSlots.some((slot) => this.twoFactorForm.get(`${this.otpPrefix}_${slot}`)?.touched);
  }

  backToSignIn(): void {
    this.authService.logout();
    this.router.navigate(['/auth/sign-in'], {
      queryParams: this.returnUrl && this.returnUrl !== '/'
        ? { returnUrl: this.returnUrl }
        : undefined,
    });
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    // Defer keyboard handling to NgbModal when present
    if (!this.isModalMode || this.activeModal) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      this.closeModal();
    }
    if (event.key === 'Tab' && this.trapActive) {
      this.maintainFocusTrap(event);
    }
  }

  private initFocusTrap(): void {
    const root = this.dialogRoot?.nativeElement;
    if (!root) return;
    this.focusableElements = Array.from(root.querySelectorAll<HTMLElement>([
      'a[href]',
      'area[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button:not([disabled])',
      'iframe',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(',')));

    const toFocus = this.focusableElements[0] || root;
    setTimeout(() => toFocus.focus(), 0);
    this.trapActive = true;
  }

  private maintainFocusTrap(event: KeyboardEvent): void {
    if (!this.focusableElements.length) return;
    const first = this.focusableElements[0];
    const last = this.focusableElements[this.focusableElements.length - 1];
    const active = document.activeElement as HTMLElement | null;

    if (event.shiftKey) {
      if (active === first || !this.dialogRoot?.nativeElement.contains(active)) {
        last.focus();
        event.preventDefault();
      }
    } else {
      if (active === last) {
        first.focus();
        event.preventDefault();
      }
    }
  }

  private teardownFocusTrap(): void {
    this.trapActive = false;
    this.focusableElements = [];
  }

  private activateScrollLock(): void {
    // Mobile-friendly scroll lock
    const body = document.body;
    this.originalScrollY = window.scrollY || window.pageYOffset || 0;
    body.style.top = `-${this.originalScrollY}px`;
    body.style.position = 'fixed';
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overscrollBehavior = 'contain';
    (body.style as any).webkitOverflowScrolling = 'auto';
  }

  private deactivateScrollLock(): void {
    const body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    body.style.overscrollBehavior = '';
    (body.style as any).webkitOverflowScrolling = '';
    window.scrollTo(0, this.originalScrollY || 0);
  }

  initForm(): void {
    this.twoFactorForm = this.formBuilder.group({
      code_1: ['', [Validators.required, Validators.pattern(/^[0-9]$/)]],
      code_2: ['', [Validators.required, Validators.pattern(/^[0-9]$/)]],
      code_3: ['', [Validators.required, Validators.pattern(/^[0-9]$/)]],
      code_4: ['', [Validators.required, Validators.pattern(/^[0-9]$/)]],
      code_5: ['', [Validators.required, Validators.pattern(/^[0-9]$/)]],
      code_6: ['', [Validators.required, Validators.pattern(/^[0-9]$/)]],
      backupCode: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(12)]],
      emailCode_1: ['', [Validators.required, Validators.pattern(/^[0-9]$/)]],
      emailCode_2: ['', [Validators.required, Validators.pattern(/^[0-9]$/)]],
      emailCode_3: ['', [Validators.required, Validators.pattern(/^[0-9]$/)]],
      emailCode_4: ['', [Validators.required, Validators.pattern(/^[0-9]$/)]],
      emailCode_5: ['', [Validators.required, Validators.pattern(/^[0-9]$/)]],
      emailCode_6: ['', [Validators.required, Validators.pattern(/^[0-9]$/)]]
    });
  }

  loadTwoFactorSetup(): void {
    // Never attempt to (re)generate setup here; only fetch info when applicable
    this.authService.getTwoFactorSetup()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            // Handle the EF-IT Portal API response structure
            const data = response.data;
            this.qrCodeUrl = data.qr || data.qr_code_url || data.qrCodeUrl || '';
            this.backupCodes = data.backup_codes || data.backupCodes || [];
            this.secretKey = data.secret || '';
            
          } else {
            // Do not surface error here when setup is already completed
          }
        },
        error: (error) => {
          // If backend returns 409 (already set up), silently ignore
          if (error?.status === 409) {
            return;
          }
          this.hasError = true;
          this.errorMessage = error.message || 'An error occurred while loading two-factor setup';
        }
      });
  }

  submit(): void {
    if (this.twoFactorForm.invalid) {
      return;
    }

    this.hasError = false;
    this.errorMessage = '';

    if (this.mfaType === 'totp') {
      const code = this.getCodeFromInputs('code');
      this.verifyTotp(code);
    } else if (this.mfaType === 'email') {
      const code = this.getCodeFromInputs('emailCode');
      this.verifyEmail(code);
    }
  }

  getCodeFromInputs(prefix: string): string {
    const code1 = this.twoFactorForm.get(`${prefix}_1`)?.value || '';
    const code2 = this.twoFactorForm.get(`${prefix}_2`)?.value || '';
    const code3 = this.twoFactorForm.get(`${prefix}_3`)?.value || '';
    const code4 = this.twoFactorForm.get(`${prefix}_4`)?.value || '';
    const code5 = this.twoFactorForm.get(`${prefix}_5`)?.value || '';
    const code6 = this.twoFactorForm.get(`${prefix}_6`)?.value || '';
    return code1 + code2 + code3 + code4 + code5 + code6;
  }

  verifyTotp(code: string): void {
    this.authService.verifyTwoFactor(code)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.clearMfaPendingFlags();
            if (this.isModalMode) {
              this.mfaMonitorService.onMfaVerificationSuccess();
              if (this.activeModal) {
                this.activeModal.close(true);
              }
            } else {
              enterPortalApp(this.returnUrl || '/');
            }
          } else {
            this.handleVerificationFailure('code', response.message || 'Invalid verification code');
          }
        },
        error: (error) => {
          this.handleVerificationFailure('code', error.message || 'An error occurred during verification');
        }
      });
  }

  verifyEmail(code: string): void {
    this.authService.verifyEmailMfa(code)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.clearMfaPendingFlags();
            if (this.isModalMode) {
              this.mfaMonitorService.onMfaVerificationSuccess();
              if (this.activeModal) {
                this.activeModal.close(true);
              }
            } else {
              enterPortalApp(this.returnUrl || '/');
            }
          } else {
            this.handleVerificationFailure('emailCode', response.message || 'Invalid email verification code');
          }
        },
        error: (error) => {
          this.handleVerificationFailure('emailCode', error.message || 'An error occurred during email verification');
        }
      });
  }

  private clearMfaPendingFlags(): void {
    try {
      localStorage.removeItem('mfaPending');
      localStorage.removeItem('mfaEnrollRequired');
      localStorage.setItem('lastAuthMethod', this.mfaType === 'email' ? 'email_mfa' : 'totp');
      localStorage.setItem('lastAuthAt', String(Date.now()));
    } catch {}
  }

  resendCode(): void {
    if (this.mfaType !== 'email') {
      return;
    }

    this.authService.requestEmailMfa()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.emailSent = true;
          } else {
            this.hasError = true;
            this.errorMessage = response.message || 'Failed to send email code';
          }
        },
        error: (error) => {
          this.hasError = true;
          this.errorMessage = error.message || 'An error occurred while sending email code';
        }
      });
  }

  useBackupCode(code: string): void {
    this.authService.verifyTwoFactor(code)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            if (this.isModalMode) {
              this.mfaMonitorService.onMfaVerificationSuccess();
              if (this.activeModal) {
                this.activeModal.close(true);
              }
            } else {
              enterPortalApp(this.returnUrl);
            }
          } else {
            this.hasError = true;
            this.errorMessage = response.message || 'Invalid backup code';
          }
        },
        error: (error) => {
          this.hasError = true;
          this.errorMessage = error.message || 'Backup code verification failed';
        }
      });
  }

  showBackupCodeInput(): void {
    this.showBackupCodeField = true;
    this.hasError = false;
    this.errorMessage = '';
  }

  submitBackupCode(): void {
    if (this.twoFactorForm.get('backupCode')?.invalid) {
      return;
    }

    const backupCode = this.twoFactorForm.get('backupCode')?.value;
    this.useBackupCode(backupCode);
  }

  switchToEmailMfa(): void {
    this.mfaType = 'email';
    this.hasError = false;
    this.errorMessage = '';
    this.emailSent = false;
    // setup-email now enables the method and sends the verification code
    this.setupEmailMfa();
  }

  switchToTotpMfa(): void {
    this.mfaType = 'totp';
    this.hasError = false;
    this.errorMessage = '';
    this.emailSent = false;
  }

  setupEmailMfa(): void {
    this.authService.setupEmailMfa()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.emailSent = true;
            this.hasError = false;
            this.errorMessage = '';
          } else {
            this.emailSent = false;
            this.hasError = true;
            this.errorMessage = response.message || 'Failed to send verification email';
          }
        },
        error: (error) => {
          this.emailSent = false;
          this.hasError = true;
          this.errorMessage = error?.error?.error || error.message || 'Failed to send verification email';
        }
      });
  }

  copyToClipboard(text: string): void {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).catch(() => undefined);
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
      } catch {
        // ignore
      }
      document.body.removeChild(textArea);
    }
  }

  onCodeInput(event: any, currentIndex: number, prefix: string): void {
    const input = event.target;
    const value = input.value;
    
    // Only allow numbers
    if (!/^[0-9]$/.test(value)) {
      input.value = '';
      return;
    }

    // Auto-focus to next input
    if (value && currentIndex < 6) {
      const nextInput = document.querySelector(`input[name="${prefix}_${currentIndex + 1}"]`) as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }

    // Auto-submit when all 6 digits are filled
    if (this.isCodeValid(prefix)) {
      const code = this.getCodeFromInputs(prefix === 'emailCode' ? 'emailCode' : 'code');
      if (prefix === 'emailCode') {
        this.verifyEmail(code);
      } else {
        this.verifyTotp(code);
      }
    }
  }

  onCodeKeydown(event: any, currentIndex: number, prefix: string): void {
    // Handle backspace to go to previous input
    if (event.key === 'Backspace' && !event.target.value && currentIndex > 1) {
      const prevInput = document.querySelector(`input[name="${prefix}_${currentIndex - 1}"]`) as HTMLInputElement;
      if (prevInput) {
        prevInput.focus();
      }
    }
  }

  isCodeValid(prefix: string): boolean {
    for (let i = 1; i <= 6; i++) {
      const control = this.twoFactorForm.get(`${prefix}_${i}`);
      if (!control?.valid) {
        return false;
      }
    }
    return true;
  }

  onCodePaste(event: ClipboardEvent, prefix: string): void {
    const clipboard = event.clipboardData?.getData('text') || '';
    const digits = clipboard.replace(/\D/g, '').slice(0, 6).split('');
    if (digits.length === 0) {
      return;
    }
    event.preventDefault();
    for (let i = 1; i <= 6; i++) {
      const control = this.twoFactorForm.get(`${prefix}_${i}`);
      control?.setValue(digits[i - 1] || '');
    }
    const focusIndex = Math.min(digits.length + 1, 6);
    const nextInput = document.querySelector(`input[name="${prefix}_${focusIndex}"]`) as HTMLInputElement;
    if (nextInput) {
      nextInput.focus();
    }

    // Auto-submit on full paste
    if (digits.length === 6 && this.isCodeValid(prefix)) {
      const code = this.getCodeFromInputs(prefix === 'emailCode' ? 'emailCode' : 'code');
      if (prefix === 'emailCode') {
        this.verifyEmail(code);
      } else {
        this.verifyTotp(code);
      }
    }
  }

  getInputBorderColor(index: number, prefix: string): string | null {
    const control = this.twoFactorForm.get(`${prefix}_${index}`);
    const hasValue = !!control?.value;
    const allFilled = this.isCodeValid(prefix);
    return hasValue && !allFilled ? 'var(--bs-warning)' : null;
  }

  private handleVerificationFailure(prefix: string, message: string): void {
    this.hasError = true;
    this.errorMessage = message;
    this.clearCodeInputs(prefix);
    this.animateShake();
    // Focus the first input for quick re-entry
    const firstInput = document.querySelector(`input[name="${prefix}_1"]`) as HTMLInputElement;
    if (firstInput) {
      firstInput.focus();
    }
  }

  private clearCodeInputs(prefix: string): void {
    for (let i = 1; i <= 6; i++) {
      const control = this.twoFactorForm.get(`${prefix}_${i}`);
      control?.setValue('');
      control?.markAsPristine();
      control?.markAsUntouched();
    }
  }

  private animateShake(): void {
    this.shake = false; // restart animation
    // next tick to allow class reflow
    setTimeout(() => {
      this.shake = true;
      setTimeout(() => (this.shake = false), 500);
    }, 0);
  }


}
